import { Component,Input,OnInit,ViewEncapsulation } from '@angular/core';
import { FormBuilder,FormControl,FormGroup,Validators } from '@angular/forms';
import { forkJoin,from,of } from 'rxjs';
import { catchError,concatMap,filter,finalize,map,switchMap,toArray } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { GroupShare } from 'src/app/models/services/admin/groups-share/groupShare';
import { GroupShareBusiness } from 'src/app/models/services/admin/groups-share/groupShareBusiness';
import { GroupShareItem } from 'src/app/models/services/admin/groups-share/groupShareItem';
import { GroupShareDialogModeEnum } from 'src/app/models/services/admin/groups-share/GroupShareDialogModeEnum.enum';
import { GroupShareDialogCloseEnum } from 'src/app/models/services/admin/groups-share/GroupShareDialogCloseEnum.enum';
import { GroupShareListSideEnum } from 'src/app/models/services/admin/groups-share/GroupShareListSideEnum.enum';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';
import { TikTokAudiencesService } from 'src/app/services/audiences/tiktok-audiences.service';
import { AmazonAudiencesService } from 'src/app/services/audiences/amazon-audiences.service';


export type GroupShareDialogClose =
  | { action: GroupShareDialogCloseEnum.Cancel; }
  | { action: GroupShareDialogCloseEnum.CreateGroupShare; data: GroupShare; }
  | { action: GroupShareDialogCloseEnum.EditGroupShare; id: string; data: GroupShare; newId?: string; };

interface GroupSharePlatformOption {
  id: number;
  name: string;
  icon: string;
}

interface BusinessListState {
  businesses: GroupShareBusiness[];
  search: string;
  page: number;
  pageSize: number;
  selectedKeys: Set<string>;
  expandedIds: Set<string>;
}

const AVAILABLE_PLATFORMS: GroupSharePlatformOption[] = [
  {
    id: TargetPlatforms.TikTok,
    name: 'TikTok',
    icon: 'assets/images/target-platforms/3.png'
  },
  {
    id: TargetPlatforms.Amazon,
    name: 'Amazon',
    icon: 'assets/images/target-platforms/7.png'
  },
  {
    id: TargetPlatforms.Meta,
    name: 'Meta',
    icon: 'assets/images/target-platforms/2.png'
  }
];

@Component({
  selector: 'groups-share-dialog',
  templateUrl: './groups-share-dialog.component.html',
  styleUrls: ['./groups-share-dialog.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class GroupShareDialogComponent implements OnInit {

  @Input() mode: GroupShareDialogModeEnum = GroupShareDialogModeEnum.CreateGroupShare;
  @Input() groupShare: GroupShare | null = null;

  accept: (result: GroupShareDialogClose) => void = () => {};
  dismiss: () => void = () => {};

  title = '';
  loadingBusinesses = false;
  readonly availablePlatforms = AVAILABLE_PLATFORMS;
  readonly GroupShareDialogModeEnum = GroupShareDialogModeEnum;
  readonly GroupShareListSideEnum = GroupShareListSideEnum;
  private readonly businessesCache = new Map<number, GroupShareBusiness[]>();
  private readonly listSides: GroupShareListSideEnum[] = [
    GroupShareListSideEnum.Available,
    GroupShareListSideEnum.Group
  ];
  readonly form: FormGroup;
  readonly lists: Record<GroupShareListSideEnum, BusinessListState> = {
    [GroupShareListSideEnum.Available]: {
      businesses: [],
      search: '',
      page: 1,
      pageSize: 3,
      selectedKeys: new Set<string>(),
      expandedIds: new Set<string>()
    },

    [GroupShareListSideEnum.Group]: {
      businesses: [],
      search: '',
      page: 1,
      pageSize: 3,
      selectedKeys: new Set<string>(),
      expandedIds: new Set<string>()
    }
  };

  constructor(
    formBuilder: FormBuilder,
    private tikTokAudiencesService: TikTokAudiencesService,
    private amazonAudiencesService: AmazonAudiencesService,
    private spinnerService: NgxSpinnerService
  ) {
    this.form = formBuilder.group({
      groupName: ['', Validators.required],
      targetPlatform: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    const isEditing = this.mode === GroupShareDialogModeEnum.EditGroupShare && !!this.groupShare;
    const groupBusinesses = isEditing ? this.groupShare!.businesses ?? [] : [];
    const platformId = (isEditing ? Number(this.groupShare!.targetPlatform) : null) || this.availablePlatforms[0]?.id || null;
    this.title = isEditing ? 'Edit group' : 'Create group';
    this.form.patchValue({ groupName: isEditing ? this.groupShare!.name : '', targetPlatform: platformId });
    this.lists[GroupShareListSideEnum.Group].businesses = this.cloneBusinesses(groupBusinesses);
    this.loadPlatformBusinesses(Number(platformId));
  }

  get groupNameControl(): FormControl {
    return this.form.get('groupName') as FormControl;
  }

  get groupItemsCount(): number {
    return this.lists[GroupShareListSideEnum.Group].businesses.reduce((total, business) => total + (business.items?.length ?? 0),0);
  }

  selectPlatform(platform: GroupSharePlatformOption): void {
    if (Number(this.form.get('targetPlatform')?.value) === platform.id) {
      return;
    }
    this.form.get('targetPlatform')?.setValue(platform.id);
    this.form.get('targetPlatform')?.markAsTouched();
    this.lists[GroupShareListSideEnum.Group].businesses = [];
    this.lists[GroupShareListSideEnum.Available].search = '';
    this.lists[GroupShareListSideEnum.Group].search = '';
    this.loadPlatformBusinesses(platform.id);
  }


  isPlatformSelected(platformId: number): boolean {
    return Number(this.form.get('targetPlatform')?.value) === platformId;
  }


  getBusinesses(side: GroupShareListSideEnum,paginate = false): GroupShareBusiness[] {
    const list = this.lists[side];
    const search = this.normalize(list.search.trim());
    const businesses = search ? list.businesses.filter(
            business =>
              this.normalize(business.id).includes(search) ||
              this.normalize(business.name).includes(search) ||
              (business.items ?? []).some(item => this.normalize(item.id).includes(search) || this.normalize(item.name).includes(search))
          )
        : list.businesses;
    if (!paginate) {
      return businesses;
    }
    const start =
      (list.page - 1) *
      list.pageSize;


    return businesses.slice(
      start,
      start + list.pageSize
    );
  }

  onSearchChange(side: GroupShareListSideEnum): void {
    this.lists[side].page = 1;
  }

  toggleExpanded(side: GroupShareListSideEnum,businessId: string): void {
    const expandedIds = this.lists[side].expandedIds;
    const id = String(businessId);
    if (expandedIds.has(id)) {
      expandedIds.delete(id);
      return;
    }
    expandedIds.add(id);
  }

  isExpanded(side: GroupShareListSideEnum,businessId: string): boolean {
    return this.lists[side].expandedIds.has(String(businessId));
  }

  toggleSelection(side: GroupShareListSideEnum,business: GroupShareBusiness,item?: GroupShareItem): void {
    const selectedKeys = this.lists[side].selectedKeys;
    if (item) {
      const key = this.itemKey(business.id,item.id);
      if (selectedKeys.has(key)) {
        selectedKeys.delete(key);
      } else {
        selectedKeys.add(key);
      }
      return;
    }
    const selectAll = !this.isSelected(side,business);
    (business.items ?? []).forEach(
      businessItem => {
        const key = this.itemKey(business.id,businessItem.id);
        if (selectAll) {
          selectedKeys.add(key);
        } else {
          selectedKeys.delete(key);
        }
      }
    );
  }

  isSelected(side: GroupShareListSideEnum,business: GroupShareBusiness,item?: GroupShareItem): boolean {
    const selectedKeys = this.lists[side].selectedKeys;
    if (item) {
      return selectedKeys.has(this.itemKey(business.id,item.id));
    }
    const items = business.items ?? [];
    return (items.length > 0 && items.every(businessItem => selectedKeys.has(this.itemKey(business.id,businessItem.id))));
  }

  isPartiallySelected(side: GroupShareListSideEnum,business: GroupShareBusiness): boolean {
    const selectedKeys = this.lists[side].selectedKeys;
    const items = business.items ?? [];
    const selectedItems = items.filter(item => selectedKeys.has(this.itemKey(business.id,item.id))).length;
    return (selectedItems > 0 && selectedItems < items.length);
  }

  moveSelected(from: GroupShareListSideEnum): void {
    const to = from === GroupShareListSideEnum.Available ? GroupShareListSideEnum.Group : GroupShareListSideEnum.Available;
    const selectedKeys = this.lists[from].selectedKeys;
    if (!selectedKeys.size) {
      return;
    }
    const movedBusinesses: GroupShareBusiness[] = [];
    this.lists[from].businesses = this.lists[from].businesses
        .map(business => {
          const items = business.items ?? [];
          const movedItems = items.filter(item => selectedKeys.has(this.itemKey(business.id,item.id)));
          const remainingItems = items.filter(item => !selectedKeys.has(this.itemKey(business.id,item.id)));
          if (movedItems.length) {
            movedBusinesses.push({
              ...business,
              items: movedItems.map(item => ({ ...item }))
            });
          }
          return { ...business, items: remainingItems.map(item => ({ ...item }))};
        }).filter(business => business.items.length > 0);
    movedBusinesses.forEach(
      movedBusiness => {
        const existingBusiness = this.lists[to].businesses.find(business => String(business.id) === String(movedBusiness.id));
        if (!existingBusiness) {
          this.lists[to].businesses.push(...this.cloneBusinesses([movedBusiness]));
          return;
        }
        const existingItemIds = new Set(existingBusiness.items.map(item => String(item.id)));
        existingBusiness.items.push(
          ...movedBusiness.items.filter(item => !existingItemIds.has(String(item.id))).map(item => ({...item}))
        );
      }
    );
    this.listSides.forEach(
      side => {
        const list = this.lists[side];
        list.selectedKeys.clear();
        list.page = Math.min(list.page,Math.max(1, Math.ceil(this.getBusinesses(side).length / list.pageSize)));
      }
    );
  }

  onAccept(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.groupItemsCount || (this.mode === GroupShareDialogModeEnum.EditGroupShare && !this.groupShare)) {
      return;
    }
    const {groupName,targetPlatform} = this.form.getRawValue();
    const data: GroupShare = {
      id:this.groupShare?.id ?? 0,
      name: String(groupName).trim(),
      targetPlatform: Number(targetPlatform),
      businesses: this.cloneBusinesses(this.lists[GroupShareListSideEnum.Group].businesses)
    };
    if (this.mode === GroupShareDialogModeEnum.CreateGroupShare) {
      this.accept({ action: GroupShareDialogCloseEnum.CreateGroupShare,data });
      return;
    }
    this.accept({ action: GroupShareDialogCloseEnum.EditGroupShare, id: String(this.groupShare!.id), data });
  }

  private loadPlatformBusinesses(platformId: number): void {
    const cachedBusinesses = this.businessesCache.get(platformId);
    if (cachedBusinesses) {
      this.setAvailableBusinesses(this.cloneBusinesses(cachedBusinesses));
      this.loadingBusinesses = false;
      return;
    }
    this.loadingBusinesses = true;
    if (platformId === TargetPlatforms.TikTok) {
      this.loadTikTokBusinesses();
      return;
    }
    if (platformId === TargetPlatforms.Amazon) {
      this.loadAmazonBusinesses();
      return;
    }
    this.setAvailableBusinesses([]);
    this.loadingBusinesses = false;
  }


  private loadTikTokBusinesses(): void {
    this.spinnerService.show('dialogSpinner');
    this.tikTokAudiencesService.getBusiness()
      .pipe(
        switchMap(
          businesses => {
            if (!businesses?.length) {
              return of([] as GroupShareBusiness[]);
            }
            const requests = businesses.map(
                business =>
                  this.tikTokAudiencesService.getAdvertisers(String(business.id))
                    .pipe(
                      map(
                        advertisers => ({
                          id: String(business.id),
                          name: business.name ?? '',
                          items: (advertisers ?? []).map(
                            advertiser => ({
                                id: String(advertiser.id),
                                name: advertiser.name ?? '',
                                type: 'advertiser'
                              })
                            )
                        } as GroupShareBusiness)
                      ),
                      catchError(
                        () =>
                          of({
                            id: String(business.id),
                            name: business.name ?? '',
                            items: []
                          } as GroupShareBusiness)
                      )
                    )
              );
            return forkJoin(requests);
          }
        ),
        finalize(
          () => {
            this.loadingBusinesses = false;
            this.spinnerService.hide('dialogSpinner');
          }
        )
      )
      .subscribe({
        next: businesses => {
          this.businessesCache.set(TargetPlatforms.TikTok,this.cloneBusinesses(businesses));
          this.setAvailableBusinesses(businesses);
        },
        error: () => {
          this.setAvailableBusinesses([]);
        }
      }
    );
  }

  private loadAmazonBusinesses(): void {
    this.spinnerService.show('dialogSpinner');
    this.amazonAudiencesService.getProfiles()
      .pipe(
        switchMap(
          profiles => {
            if (!profiles?.length) {
              return of([] as GroupShareBusiness[]);
            }
            return from(profiles).pipe(
              concatMap(
                profile => {
                  const rawProfile = profile as any;
                  const profileId = Number(rawProfile.profileId ?? rawProfile.id);
                  const profileName = String(rawProfile.accountInfo?.name ?? rawProfile.name ?? profileId);
                  if (!Number.isFinite(profileId)) {
                    return of(null);
                  }
                  return this.amazonAudiencesService.getAdvertisers(profileId,true)
                    .pipe(
                      map(
                        advertisers => {
                          const items: GroupShareItem[] = (advertisers ?? [])
                            .map(
                              advertiser => {
                                const rawAdvertiser = advertiser as any;
                                return {
                                  id:
                                    String(
                                      rawAdvertiser.dspAdvertiserId ??
                                      rawAdvertiser.advertiserId ??
                                      rawAdvertiser.id ??
                                      rawAdvertiser.accountId ??
                                      ''
                                    ),
                                  name:
                                    String(
                                      rawAdvertiser.accountName ??
                                      rawAdvertiser.advertiserName ??
                                      rawAdvertiser.name ??
                                      ''
                                    ),
                                  type: 'advertiser'
                                } as GroupShareItem;
                              }
                            ).filter(item => !!item.id);
                          if (!items.length) {
                            return null;
                          }
                          return { id: String(profileId),name: profileName,items} as GroupShareBusiness;
                        }
                      ),
                      catchError(
                        () => of(null)
                      )
                    );
                }
              ),
              filter((business): business is GroupShareBusiness => business !== null), toArray()
            );
          }
        ),
        finalize(
          () => {
            this.loadingBusinesses = false;
            this.spinnerService.hide('dialogSpinner');
          }
        )
      )
      .subscribe({
        next: (businesses: GroupShareBusiness[]) => {
          this.businessesCache.set(TargetPlatforms.Amazon,
          this.cloneBusinesses(businesses));
          this.setAvailableBusinesses(businesses);
        },
        error: () => {
          this.setAvailableBusinesses([]);
        }
      }
    );
  }

  private setAvailableBusinesses(businesses: GroupShareBusiness[]): void {
    const groupList = this.lists[GroupShareListSideEnum.Group];
    const availableList = this.lists[GroupShareListSideEnum.Available];
    const groupKeys = new Set<string>();
    groupList.businesses.forEach(
      business => {
        (business.items ?? []).forEach(
          item => { groupKeys.add(this.itemKey(business.id,item.id)); }
        );
      }
    );
    availableList.businesses =
      this.cloneBusinesses(businesses)
        .map(
          business => ({
            ...business,
            items: (business.items ?? []).filter(item => !groupKeys.has(this.itemKey(business.id,item.id)))
          })
        ).filter(business => business.items.length > 0);
    availableList.page = 1;
    groupList.page = 1;
    availableList.selectedKeys.clear();
    groupList.selectedKeys.clear();
    availableList.expandedIds.clear();
    groupList.expandedIds.clear();
  }


  private normalize(value: unknown): string {
    return String(value ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'');
  }

  private itemKey(businessId: string,itemId: string): string {
    return `${businessId}::${itemId}`;
  }

  private cloneBusinesses(businesses: GroupShareBusiness[] | null | undefined): GroupShareBusiness[] {
    return (businesses ?? []).map(
      business => ({
        ...business,
        items: (business.items ?? []).map(item => ({ ...item }))
      })
    );
  }
}